<template>
  <div @mouseenter="openSidebar" @mouseleave="closeSidebar">
    <div v-if="!sidebarHide && (adminViewMode.show || isTablet)" class="sidebar-overlay" @click="closeSidebar"></div>
    <div class="main-header">
      <div class="sm-header">
        <a class="collapse-btn" @click="toggleSidebar">
          <fa :icon="sidebarHide ? 'arrow-right' : 'arrow-left'" fixed-width />
        </a>
      </div>
      <div
        v-if="!editModeMenus.shopByGalleryEditMode"
        class="app-sidebar"
        :style="{ top: effectiveSidebarTop, height: `calc(100vh - ${effectiveSidebarTop})` }"
        :class="[{ 'admin-sidebar': !sidebarHide && adminViewMode.show }, { 'active-admin-view' : (sidebarHide && isAdminRoute) }]"
      >
        <div class="header">
          <a v-if="!adminViewMode.show" class="collapse-btn" @click="toggleSidebar">
            <fa :icon="sidebarHide ? 'arrow-right' : 'arrow-left'" fixed-width />
          </a>
          <div v-if="!sidebarHide && isAdmin" class="d-flex flex-column admin-toggle">
            <div class="d-flex align-items-center">
              <toggle-switch
                id="admin-view"
                class-name="me-2"
                :value="adminViewMode.show"
                @change="onAdminViewChanged"
              />
              <span class="text-black" style="font: normal normal normal 12px/14px Arial"
                >ADMIN View</span
              >
            </div>
          </div>
          <Close
            v-if="!sidebarHide && adminViewMode.show && isLaptop"
            :class="{ 'sidebar-icon': !sidebarHide && adminViewMode.show }"
            fill-color="gray"
            @click="closeSidebar()"
          />
        </div>

        <div class="d-flex flex-column justify-content-between mb-5">
          <div
            v-if="adminViewMode.show && isAdmin"
            class="approvals"
            @click="navigateToPage({ linkName: 'approvals' })"
          >
            Approvals
            <div class="count">
              {{ approvalRequestsCount }}
            </div>
          </div>
          <ul
            class="nav flex-column"
            :class="{
              'admin-sidebar-list': !sidebarHide && adminViewMode.show
            }"
          >
            <li
              v-for="(nav, index) in navItems"
              :key="index"
              class="nav-item"
              :class="{ active: routerName === nav.linkName || (nav.linkChildrenName !== undefined && nav.linkChildrenName.includes(routerName)), 'd-none': !isAdmin && !isRep && nav.linkName === 'contacts' }"
              v-if="nav.linkName !== 'order-history.index' || hasOrderHistoryPermission"
            >
              <a class="nav-link cursor-pointer" @click="navigateToPage(nav)">
                <span class="nav-icon position-relative">
                  <img :src="`/${nav.icon}`" alt="" />
                  <div
                    v-if="nav.hasPills"
                    class="position-absolute"
                    style="right: 10px; bottom: calc(50% - 20px)"
                  >
                    <pills style="outline: 2px solid white">
                      {{ authUser.tags_count }}
                    </pills>
                  </div>
                </span>
                <span class="nav-name">{{ nav.name }}</span>
              </a>
              <template v-if="!sidebarHide && adminViewMode.show">
                <ul
                  v-for="(navItem, innerIndex) in nav.pages"
                  :key="innerIndex"
                  class="sub-nav"
                >
                  <li
                    style="cursor: pointer"
                    @click="goToPage(navItem)"
                  >
                    {{ navItem.name }}
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
        <div v-if="!sidebarHide" class="d-flex flex-column" style="padding: 0 22px">
          <div class="d-flex align-items-center">
            <toggle-switch
              id="edit-mode"
              class-name="me-2"
              :value="editModeMenus.show"
              @change="onEditModeChanged"
            />
            <span class="text-black" style="font: normal normal normal 14px/16px Arial"
              >EDIT mode</span
            >
          </div>
        </div>
      </div>

      <div class="sidebar-util">
        <div
          v-if="editModeMenus.show"
          class="hide-shop-by-gallery-edit-sidebar cursor-pointer"
          :style="{ marginLeft: editModeMenus.shopByGalleryEditMode ? '28%' : '68px' }"
          @click="hideShopByGalleryEditSidebar"
        >
          <fa
            v-if="editModeMenus.show"
            icon="arrow-right"
            class="hide-fa-icon cursor-pointer"
            :style="{
              marginLeft: editModeMenus.shopByGalleryEditMode ? '28.3%' : '73px',
              transform: editModeMenus.shopByGalleryEditMode ? 'rotate(227deg)' : 'rotate(45deg)'
            }"
            @click="hideShopByGalleryEditSidebar"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SIDE_MENU_ITEMS, ADMIN_SIDE_MENU_ITEMS, ADMIN_ROUTES, LAST_NON_ADMIN_ROUTE, PERMISSIONS } from '~/constants'
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import ToggleSwitch from '~/components/core/Inputs/ToggleSwitch'
import { SET_MENUS_EDIT_MODE, SET_MENUS_ADMIN_VIEW } from '~/store/mutation-types'
import Close from '~/components/common/icons/CloseIcon.vue'
import { isTabletViewport } from '~/helpers/viewport'
import { createScrollLock } from '~/helpers/scroll-lock'

export default {
  components: { ToggleSwitch, Close },
  data: () => ({
    sidebarHide: true,
    navItems: [],
    navBarBottom: 0,
    windowWidth: window.innerWidth,
    isTablet: isTabletViewport()
  }),
  computed: {
    ...mapState({
      editModeMenus: (state) => state.menus.editMode,
      adminViewMode: (state) => state.menus.adminViewMode
    }),
    ...mapGetters({
      authUser: 'auth/user',
      isAdmin: 'auth/isAdmin',
      isAssociate: 'auth/isAssociate',
      isRep: 'auth/isRep',
      permissions: 'auth/permissions',
      approvalRequestsCount: 'approvals/approvalRequestsCount'
    }),
    routerName() {
      return this.$route.name
    },
    hasOrderHistoryPermission() {
      return this.permissions.includes(PERMISSIONS.ORDER_HISTORY)
    },
    isAdminRoute() {
      return ADMIN_ROUTES?.includes(this.$route.name)
    },
    isLaptop () {
      return this.windowWidth < 1919
    },
    // The rail is position: fixed, so it follows the header's bottom edge as the
    // header scrolls away; 'auto' would freeze it at its page-load offset. The
    // tablet drawer and the laptop admin overlay cover the header instead.
    effectiveSidebarTop () {
      if (this.isTablet || (this.isLaptop && this.adminViewMode.show)) return '0'
      return `${this.navBarBottom}px`
    },
    // True exactly when the full-screen backdrop (.sidebar-overlay) is visible —
    // same condition as its v-if, but gated on isLaptop because the overlay CSS
    // only turns visible at <=1919px. In that state the drawer is modal, so the
    // page behind it must not scroll.
    isDrawerOverlayOpen () {
      return !this.sidebarHide && (this.adminViewMode.show || this.isTablet) && this.isLaptop
    }
  },

  watch: {
    isDrawerOverlayOpen (open) {
      if (open) this.lockBodyScroll()
      else this.unlockBodyScroll()
    },
    isLaptop (val) {
      if (val) this.closeSidebar()
      else this.openSidebar()
    }
  },
  async created() {
    this._scrollLock = createScrollLock()
    if (this.isAdmin) await this.getApprovalsCount()
    const isAdminViewActivated = localStorage.getItem('active_admin_view') === 'true'
    const isAdminViewDefault = localStorage.getItem('default_admin_view') === 'true'
    if (isAdminViewActivated) {
      if (this.isLaptop) {
        this.sidebarHide = !isAdminViewDefault
      } else {
        this.sidebarHide = isAdminViewDefault
      }
      this.adminViewMode.show = isAdminViewActivated
      this.setMenusAdminView({
        show: this.adminViewMode.show
      })
      this.navItems = ADMIN_SIDE_MENU_ITEMS
    } else {
      this.navItems = SIDE_MENU_ITEMS
      this.sidebarHide = true
      localStorage.setItem('active_admin_view', false)
      document.body.classList.add('app-sidebar--hidden')
    }

    document.addEventListener('active_admin_view', (e) => {
      document.body.classList.add('app-sidebar--hidden')
      this.setMenusAdminView({
        show: false
      })
      this.adminViewMode.show = false
      this.navItems = SIDE_MENU_ITEMS
    })
  },
  async mounted() {
    window.addEventListener('resize', this.updateWindowWidth)
    window.addEventListener('scroll', this.syncTopToNavBar, { passive: true })
    this._navBar = this.$el.parentElement?.querySelector(':scope > .nav-bar')
    // The header grows after mount (menus load async), so track its size too.
    if (this._navBar) {
      this._navBarObserver = new ResizeObserver(this.syncTopToNavBar)
      this._navBarObserver.observe(this._navBar)
    }
    this.syncTopToNavBar()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.syncTopToNavBar)
    this._navBarObserver?.disconnect()
    window.removeEventListener('resize', this.updateWindowWidth)
    this.unlockBodyScroll()
  },
  methods: {
    ...mapActions({
      getApprovalsCount: 'approvals/getApprovalsCount'
    }),
    ...mapMutations({
      setMenusEditMode: `menus/${SET_MENUS_EDIT_MODE}`,
      setMenusAdminView: `menus/${SET_MENUS_ADMIN_VIEW}`
    }),
    openSidebar() {
      document.body.classList.remove('app-sidebar--hidden')
      this.sidebarHide = false
      this.$emit('toggle-sidebar')
    },
    closeSidebar() {
      localStorage.setItem('default_admin_view', false)
      if (!this.adminViewMode.show || this.isLaptop) {
        document.body.classList.add('app-sidebar--hidden')
      }
      if (this.isLaptop) {
        this.sidebarHide = true
      } else {
        this.sidebarHide = !this.adminViewMode.show
      }
      this.$emit('toggle-sidebar')
    },
    toggleSidebar() {
      document.body.classList.toggle('app-sidebar--hidden')
      this.sidebarHide = !this.sidebarHide
    },
    lockBodyScroll () {
      this._scrollLock.lock()
    },
    unlockBodyScroll () {
      this._scrollLock.unlock()
    },
    navigateToPage(nav) {
      if (this.$route.name !== nav.linkName) {
        this.$router.push({ name: nav.linkName })
      }
      if (!this.adminViewMode.show) {
        document.body.classList.add('app-sidebar--hidden')
        this.sidebarHide = true
      } else if (this.isLaptop) {
        this.closeSidebar()
      }
    },
    onEditModeChanged() {
      this.setMenusEditMode({
        show: !this.editModeMenus.show,
        shopByGalleryEditMode: !this.editModeMenus.show
      })

      if (this.editModeMenus.show) {
        this.$router.push({ name: 'shop-by-gallery' })
        document.body.classList.add('app-sidebar--hidden')
        this.sidebarHide = true
      }
    },
    onAdminViewChanged() {
      this.setMenusAdminView({
        show: !this.adminViewMode.show
      })

      if (this.adminViewMode.show) {
        this.navItems = ADMIN_SIDE_MENU_ITEMS
        localStorage.setItem('active_admin_view', true)
        localStorage.setItem('default_admin_view', true)
        this.$router.push({ name: 'admin' })
      } else {
        this.navItems = SIDE_MENU_ITEMS
        localStorage.setItem('active_admin_view', false)
        localStorage.setItem('default_admin_view', false)
        const route = localStorage.getItem(LAST_NON_ADMIN_ROUTE) || 'products'
        this.$router.push(`/${route}`)
      }
    },
    hideShopByGalleryEditSidebar() {
      this.setMenusEditMode({
        shopByGalleryEditMode: !this.editModeMenus.shopByGalleryEditMode
      })
    },
    syncTopToNavBar () {
      this.navBarBottom = this._navBar
        ? Math.max(0, Math.round(this._navBar.getBoundingClientRect().bottom))
        : 0
    },
    goToPage (nav) {
      this.$router.push({ name: nav.link, query: nav.query })
      if (this.isLaptop) {
        this.closeSidebar()
      }
    },
    updateWindowWidth () {
      this.windowWidth = window.innerWidth
      this.isTablet = isTabletViewport()
    }
  }
}
</script>

<style lang="scss" scoped>
.sidebar-util {
  .hide-shop-by-gallery-edit-sidebar {
    position: fixed;
    margin-left: 28%;
    border-style: solid;
    border-width: 45px 45px 0 0;
    border-color: #e3e2e2 transparent transparent transparent;
  }

  .hide-fa-icon {
    position: fixed;
    transform: rotate(227deg);
    color: #898989;
    font-size: 17px;
    margin-top: 5px;
    margin-left: 28.3%;
  }

  // The gallery panel keeps a fixed width on tablet, so its collapse arrow follows
  // that edge rather than a share of the screen.
  @media only screen and (max-width: 1279px) {
    .hide-shop-by-gallery-edit-sidebar {
      margin-left: min(320px, 85vw);
    }

    .hide-fa-icon {
      margin-left: calc(min(320px, 85vw) + 3px);
    }
  }
}

.sm-header {
  display: none;
}

.collapse-btn {
  display: none;

  @media only screen and (min-width: 560px) {
    display: inline-block;
  }
}

.app-sidebar {
  height: 100vh;
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .admin-toggle {
    padding: 0px 22px;
  }
}

// Admin View Side Bar

.active-admin-view {
  @media only screen and (max-width: 1919px) {
    display: none;
  }
}

.admin-sidebar {
  width: 213px;
  @media only screen and (max-width: 1919px) {
    width: 331px !important;
    position: fixed;
    top: 0;
    z-index: 10000;
  }
  .header {
    height: 29px;
    margin-top: 46px;
  }
  .admin-toggle {
    padding: 0px 18px;
  }
  &-list {
    .nav-item {
      width: 100%;
      &:hover {
        background-color: inherit;
      }
      .nav-link {
        height: 40px;
        padding: 0 7px;
        width: 100%;
        cursor: default;
      }
      .nav-name {
        font: normal normal bold 13px/15px Arial;
        font-size: 13px !important;
        margin-bottom: 6px;
      }
      .sub-nav {
        font: normal normal normal 13px/15px Arial;
        padding: 0px 32px 6px 32px;
        width: 100%;
        &:hover {
          text-decoration: underline;
          font: italic normal normal 13px/15px Arial;
        }
        li {
          cursor: default;
        }
      }
    }
  }
}

@media only screen and (max-width: 560px) {
  .sm-header {
    display: block;
    position: absolute;
    left: 0;
    top: 5px;

    a {
      padding: 0.6rem;
      color: #000;
      font-size: 1rem;
      cursor: pointer;
    }
  }
  .app-sidebar--hidden .app-sidebar {
    width: 0 !important;
  }
}
.approvals {
  padding: 0 20px;
  padding-top: 23px;
  color: #000;
  font: normal normal bold 13px / 15px Arial;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  cursor: pointer;
  .count {
    background: #16c10e;
    color: #fff;
    border-radius: 50%;
    margin-left: 3px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.sidebar-icon {
  margin-right: 42px;
  cursor: pointer;

  @media only screen and (max-width: 800px) {
    visibility: visible;
  }
}

.app-sidebar--hidden {
  .app-sidebar {
    @media only screen and (max-width: 560px) {
      width: 0 !important;
    }
  }
}

.sidebar-overlay {
  visibility: hidden;
  @media only screen and (max-width: 1919px) {
    visibility: visible;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 10000;
  }
}

.admin-sidebar {
  .header {
    @media only screen and (min-width: 1919px) {
      margin-top: 20px;
    }
  }
}
</style>
